import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuthStore } from '../hooks/useAuth'

type Tab = 'earnings' | 'macro' | 'short' | 'insider' | 'flow' | 'journal-ai'

const TABS: { key: Tab; label: string; icon: string; superOnly?: boolean }[] = [
  { key: 'earnings',   icon: '/icons/earnings.svg',    label: 'Earnings'      },
  { key: 'macro',      icon: '/icons/macro.svg',        label: 'Macro'         },
  { key: 'short',      icon: '/icons/short_interest.svg', label: 'Short Interest' },
  { key: 'insider',    icon: '/icons/insider.svg',      label: 'Insider',      superOnly: true },
  { key: 'flow',       icon: '/icons/flow.svg',          label: 'Options Flow', superOnly: true },
  { key: 'journal-ai', icon: '/icons/ai_brain.svg',     label: 'AI Coach',     superOnly: true },
]

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="eyebrow" style={{ marginBottom: 8 }}>{children}</div>
}

function Card({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--edge)',
      borderRadius: 3, padding: '16px 18px', ...style,
    }}>{children}</div>
  )
}

function Badge({ children, color = 'cash' }: { children: React.ReactNode; color?: 'cash' | 'amber' | 'red' | 'muted' }) {
  const colors: Record<string, { color: string; bg: string; border: string }> = {
    cash:  { color: 'var(--cash)',  bg: 'var(--stamp)', border: 'var(--cash-dim)' },
    amber: { color: 'var(--amber)', bg: 'rgba(212,168,67,.08)', border: 'rgba(212,168,67,.25)' },
    red:   { color: '#e05c5c',      bg: 'rgba(224,92,92,.08)',  border: 'rgba(224,92,92,.25)'  },
    muted: { color: 'var(--muted)', bg: 'transparent',          border: 'var(--edge)'          },
  }
  const c = colors[color]
  return (
    <span style={{
      fontFamily: 'Syne Mono', fontSize: '.55rem', letterSpacing: '.08em',
      color: c.color, background: c.bg, border: `1px solid ${c.border}`,
      borderRadius: 2, padding: '2px 7px', whiteSpace: 'nowrap',
    }}>{children}</span>
  )
}

// ─── Earnings Tab ─────────────────────────────────────────────────────────────
function EarningsTab() {
  const [ticker, setTicker] = useState('')
  const [searched, setSearched] = useState('')
  const navigate = useNavigate()

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['earnings', searched],
    queryFn:  () => api.earnings.ticker(searched),
    enabled:  !!searched,
  })

  function search() { if (ticker.trim()) setSearched(ticker.trim().toUpperCase()) }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <Eyebrow>Earnings Lookup</Eyebrow>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="AAPL, NVDA, TSLA..."
            style={{ flex: 1, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 12px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '1rem', outline: 'none' }}
          />
          <button onClick={search} style={{ padding: '9px 14px', flexShrink: 0, background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', letterSpacing: '.08em', cursor: 'pointer' }}>
            SEARCH
          </button>
        </div>
      </Card>

      {isLoading && <div style={{ color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.72rem', padding: 16 }}>Fetching earnings data...</div>}

      {data && (
        <>
          {data.historical?.length > 0 && (
            <Card>
              <Eyebrow>Recent Earnings — {searched}</Eyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.historical.slice(0, 6).map((e: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--edge)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <span style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--text)' }}>{e.reportDate ?? e.period}</span>
                      <span style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: 'var(--muted)' }}>{e.fiscalPeriod ?? 'Quarterly'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {e.epsActual != null && (
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem', color: 'var(--bill)' }}>EPS ${e.epsActual?.toFixed(2)}</div>
                          {e.epsEstimated && <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>est ${e.epsEstimated?.toFixed(2)}</div>}
                        </div>
                      )}
                      {e.surprise != null && (
                        <Badge color={parseFloat(e.surprise) >= 0 ? 'cash' : 'red'}>
                          {parseFloat(e.surprise) >= 0 ? '+' : ''}{e.surprise}%
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => navigate('/analyze', { state: { ticker: searched } })} style={{ marginTop: 12, width: '100%', padding: '8px', background: 'transparent', border: '1px solid var(--cash-dim)', borderRadius: 2, color: 'var(--cash)', fontFamily: 'Syne', fontWeight: 800, fontSize: '.68rem', letterSpacing: '.08em', cursor: 'pointer' }}>
                ANALYZE {searched} →
              </button>
            </Card>
          )}

          {data.filings?.length > 0 && (
            <Card>
              <Eyebrow>Recent SEC Filings</Eyebrow>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.filings.slice(0, 5).map((f: any, i: number) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--edge)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--text)' }}>{f.form}</span>
                      <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>Filed {f.filedAt}</span>
                    </div>
                    <a href={f.url} target="_blank" rel="noreferrer" style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)', textDecoration: 'none' }}>EDGAR →</a>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {!data.historical?.length && !data.filings?.length && (
            <Card><div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--muted)' }}>No earnings data found for {searched}</div></Card>
          )}
        </>
      )}
    </div>
  )
}

// ─── Macro Tab ────────────────────────────────────────────────────────────────
function MacroTab() {
  const { data: calData, isLoading: calLoading } = useQuery({
    queryKey: ['macro-calendar'],
    queryFn:  api.macro.calendar,
  })
  const { data: indData, isLoading: indLoading } = useQuery({
    queryKey: ['macro-indicators'],
    queryFn:  api.macro.indicators,
  })

  const impactColor = (impact: string) =>
    impact === 'high' ? 'red' : impact === 'medium' ? 'amber' : 'muted'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <Eyebrow>Upcoming Events</Eyebrow>
        {calLoading && <div style={{ color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.72rem' }}>Loading...</div>}
        {calData?.events?.map((e: any, i: number) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--edge)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.78rem', color: 'var(--bill)' }}>{e.name}</span>
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>{e.description}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--text)' }}>{e.date}</span>
              <Badge color={impactColor(e.impact)}>{e.impact?.toUpperCase()}</Badge>
            </div>
          </div>
        ))}
      </Card>

      <Card>
        <Eyebrow>Key Indicators</Eyebrow>
        {indLoading && <div style={{ color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.72rem' }}>Loading FRED data...</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {indData?.indicators?.map((ind: any, i: number) => (
            <div key={i} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '10px 12px' }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: 4 }}>{ind.name.toUpperCase()}</div>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.1rem', color: 'var(--bill)' }}>
                {ind.value?.toFixed(2)}{ind.id?.includes('RATE') || ind.id?.includes('CPI') || ind.id === 'UNRATE' ? '%' : ''}
              </div>
              {ind.change != null && (
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.6rem', color: ind.change >= 0 ? 'var(--cash)' : '#e05c5c', marginTop: 2 }}>
                  {ind.change >= 0 ? '+' : ''}{ind.change}% vs prev
                </div>
              )}
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.55rem', color: 'var(--muted)', marginTop: 4 }}>{ind.date}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

// ─── Short Interest Tab ───────────────────────────────────────────────────────
function ShortTab() {
  const [ticker, setTicker] = useState('')
  const [searched, setSearched] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['short', searched],
    queryFn:  () => api.short.ticker(searched),
    enabled:  !!searched,
  })

  function search() { if (ticker.trim()) setSearched(ticker.trim().toUpperCase()) }

  const squeezeColor = (score: number | null) =>
    !score ? 'muted' : score >= 70 ? 'red' : score >= 40 ? 'amber' : 'cash'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <Eyebrow>Short Interest Lookup</Eyebrow>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="GME, AMC, BBBY..."
            style={{ flex: 1, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 12px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '1rem', outline: 'none' }}
          />
          <button onClick={search} style={{ padding: '9px 14px', flexShrink: 0, background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', letterSpacing: '.08em', cursor: 'pointer' }}>
            SEARCH
          </button>
        </div>
      </Card>

      {isLoading && <div style={{ color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.72rem', padding: 16 }}>Fetching FINRA data...</div>}

      {data && (
        <>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.2rem', color: 'var(--bill)' }}>{searched}</div>
              {data.squeezeLabel && <Badge color={squeezeColor(data.squeezeScore)}>{data.squeezeLabel}</Badge>}
            </div>
            {data.latest ? (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Short Interest', value: data.latest.shortInterest ? (data.latest.shortInterest / 1e6).toFixed(1) + 'M' : 'N/A' },
                  { label: 'Avg Volume',     value: data.latest.avgVolume ? (data.latest.avgVolume / 1e6).toFixed(1) + 'M' : 'N/A' },
                  { label: 'Days to Cover',  value: data.latest.daysToCover ?? 'N/A' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '10px 12px' }}>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: 4 }}>{label.toUpperCase()}</div>
                    <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.1rem', color: 'var(--bill)' }}>{value}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--muted)' }}>{data.note ?? `No FINRA short interest data found for ${searched}. FINRA covers NASDAQ & OTC-listed stocks.`}</div>
            )}
          </Card>

          {data.history?.length > 0 && (
            <Card>
              <Eyebrow>Short Interest History</Eyebrow>
              {data.history.map((h: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--edge)' }}>
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>{h.date}</span>
                  <span style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--text)' }}>
                    {h.shortInterest ? (h.shortInterest / 1e6).toFixed(1) + 'M shares' : '—'}
                    {h.daysToCover ? ` · ${h.daysToCover}d cover` : ''}
                  </span>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  )
}

// ─── Insider Tab ──────────────────────────────────────────────────────────────
function InsiderTab() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [ticker, setTicker] = useState('')
  const [searched, setSearched] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['insider', searched],
    queryFn:  () => api.insider.ticker(searched),
    enabled:  !!searched && user?.tier === 'super',
  })

  function search() { if (ticker.trim()) setSearched(ticker.trim().toUpperCase()) }

  if (user?.tier !== 'super') return (
    <Card style={{ textAlign: 'center', padding: '32px 24px' }}>
      <img src="/icons/insider.svg" alt="" style={{width:36,height:36,opacity:.3,marginBottom:12}}/>
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', color: 'var(--bill)', marginBottom: 8 }}>Insider Transactions — Super Tier</div>
      <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
        Track executive buy/sell activity via SEC Form 4 filings.<br />Know when insiders are loading up or selling out.
      </div>
      <button onClick={() => navigate('/pricing')} style={{ padding: '10px 24px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.75rem', letterSpacing: '.08em', cursor: 'pointer' }}>
        UPGRADE TO SUPER →
      </button>
    </Card>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <Eyebrow>Insider Transactions (SEC Form 4)</Eyebrow>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="AAPL, NVDA, META..."
            style={{ flex: 1, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 12px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '1rem', outline: 'none' }}
          />
          <button onClick={search} style={{ padding: '9px 14px', flexShrink: 0, background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', letterSpacing: '.08em', cursor: 'pointer' }}>
            SEARCH
          </button>
        </div>
      </Card>

      {isLoading && <div style={{ color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.72rem', padding: 16 }}>Fetching SEC EDGAR data...</div>}

      {data && (
        <>
          {data.summary && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.1rem', color: 'var(--bill)', marginBottom: 4 }}>{searched}</div>
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>{data.summary.total} Form 4 filings in past {data.summary.period}</div>
                </div>
                <Badge color={data.summary.sentiment === 'Bullish' ? 'cash' : data.summary.sentiment === 'Bearish' ? 'red' : 'muted'}>
                  {data.summary.sentiment?.toUpperCase()}
                </Badge>
              </div>
            </Card>
          )}

          {data.transactions?.length > 0 && (
            <Card>
              <Eyebrow>Recent Form 4 Filings</Eyebrow>
              {data.transactions.slice(0, 10).map((t: any, i: number) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--edge)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--text)' }}>{t.filer}</span>
                    <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>Filed {t.filedAt} · Period {t.period}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Badge color="muted">{t.formType}</Badge>
                    <a href={t.edgarUrl} target="_blank" rel="noreferrer" style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--cash)', textDecoration: 'none' }}>EDGAR →</a>
                  </div>
                </div>
              ))}
            </Card>
          )}

          {!data.transactions?.length && (
            <Card><div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--muted)' }}>No Form 4 filings found for {searched} in the last 90 days</div></Card>
          )}
        </>
      )}
    </div>
  )
}

// ─── Options Flow Tab ─────────────────────────────────────────────────────────
function FlowTab() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const isSuper  = user?.tier === 'super'
  const [ticker, setTicker]     = useState('')
  const [searched, setSearched] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['flow', searched],
    queryFn:  () => api.flow.ticker(searched),
    enabled:  !!searched && isSuper,
  })

  function search() { if (ticker.trim()) setSearched(ticker.trim().toUpperCase()) }

  if (!isSuper) return (
    <Card style={{ textAlign: 'center', padding: '32px 24px' }}>
      <img src="/icons/flow.svg" alt="" style={{width:36,height:36,opacity:.3,marginBottom:12}}/>
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', color: 'var(--bill)', marginBottom: 8 }}>Options Flow — Super Tier</div>
      <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>
        Detect unusual options activity — large sweeps, high OI spikes, and smart money positioning.<br />Real OPRA data via Alpaca.
      </div>
      <button onClick={() => navigate('/pricing')} style={{ padding: '10px 24px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.75rem', letterSpacing: '.08em', cursor: 'pointer' }}>
        UPGRADE TO SUPER →
      </button>
    </Card>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <Eyebrow>Options Flow Scanner</Eyebrow>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && search()}
            placeholder="AAPL, SPY, QQQ..."
            style={{ flex: 1, background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '9px 12px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '1rem', outline: 'none' }}
          />
          <button onClick={search} style={{ padding: '9px 14px', flexShrink: 0, background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', letterSpacing: '.08em', cursor: 'pointer' }}>
            SCAN
          </button>
        </div>
      </Card>

      {isLoading && <div style={{ color: 'var(--muted)', fontFamily: 'Syne Mono', fontSize: '.72rem', padding: 16 }}>Fetching Alpaca options data...</div>}

      {data && (
        <>
          {data.summary && (
            <Card>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.2rem', color: 'var(--bill)' }}>{searched}</div>
                <Badge color={data.summary.flowBias === 'Bullish' ? 'cash' : data.summary.flowBias === 'Bearish' ? 'red' : 'muted'}>
                  {data.summary.flowBias?.toUpperCase()} FLOW
                </Badge>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {[
                  { label: 'Call Premium', value: data.summary.callPremium ? '$' + (data.summary.callPremium / 1e6).toFixed(1) + 'M' : 'N/A' },
                  { label: 'Put Premium',  value: data.summary.putPremium  ? '$' + (data.summary.putPremium  / 1e6).toFixed(1) + 'M' : 'N/A' },
                  { label: 'Unusual',      value: data.summary.unusualCount ?? 0 },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '10px 12px' }}>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', letterSpacing: '.1em', marginBottom: 4 }}>{label.toUpperCase()}</div>
                    <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1rem', color: 'var(--bill)' }}>{value}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {data.unusual?.length > 0 && (
            <Card>
              <Eyebrow><span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/volume_surge.svg" alt="" style={{width:11,height:11,verticalAlign:'middle'}}/> Unusual Activity</span></Eyebrow>
              {data.unusual.slice(0, 8).map((u: any, i: number) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--edge)' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <Badge color={u.type === 'call' ? 'cash' : 'red'}>{u.type?.toUpperCase()}</Badge>
                      <span style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--text)' }}>${u.strike} exp {u.expiry}</span>
                    </div>
                    <span style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
                      IV {u.impliedVolatility ?? '—'}% · Δ {u.delta ?? '—'} · OI {u.openInterest?.toLocaleString() ?? '—'}
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.82rem', color: 'var(--cash)' }}>
                      {u.premium ? '$' + (u.premium / 1e6).toFixed(2) + 'M' : '—'}
                    </div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>
                      ${u.bid}/{u.ask}
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </>
      )}
    </div>
  )
}

// ─── Alerts Tab ───────────────────────────────────────────────────────────────
function JournalAITab() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const isSuper  = user?.tier === 'super'

  const [result,    setResult]    = useState<any>(null)
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState('')

  async function runAnalysis() {
    setLoading(true); setError(''); setResult(null)
    try {
      const data = await api.journalAI.analyze()
      setResult(data)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isSuper) return (
    <Card style={{ textAlign: 'center', padding: '32px 24px' }}>
      <img src="/icons/ai_brain.svg" alt="" style={{width:36,height:36,opacity:.3,marginBottom:12}}/>
      <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.9rem', color: 'var(--bill)', marginBottom: 8 }}>AI Journal Coach</div>
      <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 16 }}>Send your trade journal to AI for deep performance coaching — win rate, pattern analysis, emotional profiling, and specific improvement recommendations.</div>
      <button onClick={() => navigate('/pricing')} style={{ padding: '10px 24px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.75rem', letterSpacing: '.08em', cursor: 'pointer' }}>UPGRADE →</button>
    </Card>
  )

  const gradeColor = (g: string) => {
    if (!g) return 'var(--muted)'
    if (g.startsWith('A')) return 'var(--cash)'
    if (g.startsWith('B')) return 'var(--amber)'
    return '#e05c5c'
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '.82rem', color: 'var(--bill)', marginBottom: 6 }}>AI Performance Coach</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', lineHeight: 1.6, marginBottom: 14 }}>
          Analyzes your real trade history — closed crypto trades and equity orders — to find patterns, strengths, behavioral tendencies, and specific improvement actions. Needs at least 3 trades.
        </div>
        {error && <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: '#e05c5c', marginBottom: 10 }}>{error}</div>}
        <button onClick={runAnalysis} disabled={loading} style={{ width: '100%', padding: '11px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.78rem', letterSpacing: '.08em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? .5 : 1 }}>
          {loading ? 'ANALYZING JOURNAL...' : 'ANALYZE MY TRADING →'}
        </button>
      </Card>

      {result?.analysis && (() => {
        const a = result.analysis
        return (
          <>
            <Card style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '2rem', color: gradeColor(a.overallGrade) }}>{a.overallGrade}</div>
                <div style={{ textAlign: 'right' }}>
                  {a.winRate != null && <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--cash)' }}>{a.winRate}% win rate</div>}
                  {a.avgRR  != null && <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>{a.avgRR}:1 avg R:R</div>}
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.58rem', color: 'var(--muted)' }}>{result.entryCount} entries analyzed</div>
                </div>
              </div>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--text)', lineHeight: 1.6 }}>{a.headline}</div>
            </Card>

            <Card style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)' }}>
              <Eyebrow>Key Insight</Eyebrow>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.72rem', color: 'var(--text)', lineHeight: 1.7 }}>{a.keyInsight}</div>
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Card>
                <Eyebrow><span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/check.svg" alt="" style={{width:11,height:11,verticalAlign:'middle'}}/> Strengths</span></Eyebrow>
                {a.strengths?.map((s: string, i: number) => (
                  <div key={i} style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--text)', lineHeight: 1.6, padding: '5px 0', borderBottom: '1px solid var(--edge)' }}>· {s}</div>
                ))}
              </Card>
              <Card>
                <Eyebrow><span style={{display:'flex',alignItems:'center',gap:5}}><img src="/icons/warning.svg" alt="" style={{width:11,height:11,verticalAlign:'middle'}}/> Weaknesses</span></Eyebrow>
                {a.weaknesses?.map((w: string, i: number) => (
                  <div key={i} style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--text)', lineHeight: 1.6, padding: '5px 0', borderBottom: '1px solid var(--edge)' }}>· {w}</div>
                ))}
              </Card>
            </div>

            {a.patterns?.length > 0 && (
              <Card>
                <Eyebrow>Patterns Detected</Eyebrow>
                {a.patterns.map((p: any, i: number) => (
                  <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--edge)' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.75rem', color: 'var(--bill)' }}>{p.title}</span>
                      <Badge color={p.impact === 'positive' ? 'cash' : p.impact === 'negative' ? 'red' : 'muted'}>{p.impact}</Badge>
                    </div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.63rem', color: 'var(--muted)', lineHeight: 1.6 }}>{p.description}</div>
                  </div>
                ))}
              </Card>
            )}

            {a.recommendations?.length > 0 && (
              <Card>
                <Eyebrow>Recommendations</Eyebrow>
                {a.recommendations.map((r: any, i: number) => (
                  <div key={i} style={{ padding: '9px 0', borderBottom: '1px solid var(--edge)' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                      <Badge color={r.priority === 'high' ? 'red' : r.priority === 'medium' ? 'amber' : 'muted'}>{r.priority?.toUpperCase()}</Badge>
                      <span style={{ fontFamily: 'Syne', fontWeight: 700, fontSize: '.75rem', color: 'var(--bill)' }}>{r.action}</span>
                    </div>
                    <div style={{ fontFamily: 'Syne Mono', fontSize: '.63rem', color: 'var(--muted)', lineHeight: 1.6 }}>{r.rationale}</div>
                  </div>
                ))}
              </Card>
            )}

            {a.emotionalProfile && (
              <Card>
                <Eyebrow>Emotional Profile</Eyebrow>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--text)', lineHeight: 1.7 }}>{a.emotionalProfile}</div>
              </Card>
            )}

            {a.encouragement && (
              <Card style={{ background: 'rgba(133,196,124,.04)', border: '1px solid var(--cash-dim)' }}>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--cash)', lineHeight: 1.7, fontStyle: 'italic' }}>"{a.encouragement}"</div>
              </Card>
            )}
          </>
        )
      })()}
    </div>
  )
}

// ─── Main Intelligence Page ───────────────────────────────────────────────────
export default function Intelligence() {
  const [tab, setTab] = useState<Tab>('earnings')
  const { user } = useAuthStore()

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.5rem', color: 'var(--bill)', marginBottom: 4 }}>Intelligence</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)' }}>Earnings · Macro · Short Interest · Insider · Flow · Alerts · AI Coach</div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 20 }}>
        {TABS.map(t => {
          const locked = t.superOnly && user?.tier !== 'super'
          return (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '6px 12px',
              background: tab === t.key ? 'var(--stamp)' : 'transparent',
              border: `1px solid ${tab === t.key ? 'var(--cash-dim)' : 'var(--edge)'}`,
              borderRadius: 2,
              color: tab === t.key ? 'var(--cash)' : 'var(--muted)',
              fontFamily: 'Syne Mono', fontSize: '.62rem', letterSpacing: '.05em',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              <span style={{display:'flex',alignItems:'center',gap:5}}><img src={t.icon} alt="" style={{width:13,height:13,verticalAlign:'middle',opacity: locked ? .4 : .85}}/>{t.label}{locked ? ' ·🔒' : ''}</span>
            </button>
          )
        })}
      </div>

      {/* Tab content */}
      {tab === 'earnings'    && <EarningsTab />}
      {tab === 'macro'       && <MacroTab />}
      {tab === 'short'       && <ShortTab />}
      {tab === 'insider'     && <InsiderTab />}
      {tab === 'flow'        && <FlowTab />}
      {tab === 'journal-ai'  && <JournalAITab />}
    </div>
  )
}
